import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../context/TranslationContext';
import '../Settings/Modal.css';

const MasterDataModal = ({ title, item, fields, idField, api, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Convert snake_case to camelCase
  const snakeToCamel = (str) => {
    return str.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
  };

  // Convert camelCase to snake_case
  const camelToSnake = (str) => {
    return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
  };

  // Initialize form data - HOOK MUST BE CALLED UNCONDITIONALLY
  useEffect(() => {
    if (!fields || !Array.isArray(fields)) {
      console.error('MasterDataModal: fields prop is required and must be an array');
      return;
    }

    const initialData = {};
    
    if (item) {
      // Map database fields (snake_case) to form fields (camelCase)
      fields.forEach(field => {
        const dbFieldName = camelToSnake(field.name);
        const value = item[dbFieldName] !== undefined ? item[dbFieldName] : (item[field.name] || '');
        initialData[field.name] = value;
      });
      console.log('Edit mode - Initialized with:', initialData);
    } else {
      // Initialize empty form
      fields.forEach(field => {
        initialData[field.name] = field.defaultValue !== undefined ? field.defaultValue : '';
      });
      console.log('Create mode - Initialized with:', initialData);
    }
    
    setFormData(initialData);
    setHasChanges(false);
  }, [item, fields]);

  // ESC key handler - HOOK MUST BE CALLED UNCONDITIONALLY
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [hasChanges]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : (type === 'number' ? (value === '' ? '' : Number(value)) : value);
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    setHasChanges(true);
  };

  const handleClose = () => {
    if (hasChanges) {
      if (!window.confirm(t('messages.confirm_close', 'You have unsaved changes. Are you sure you want to close?'))) {
        return;
      }
    }
    onClose(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    console.log('Submitting form data:', formData);
    
    // Validation
    if (!fields || !Array.isArray(fields)) {
      setError('Configuration error: fields prop is missing');
      return;
    }

    const requiredFields = fields.filter(f => f.required);
    for (const field of requiredFields) {
      if (!formData[field.name] && formData[field.name] !== 0) {
        setError(t('validation.required_fields', `${t(field.label)} is required`));
        return;
      }
    }
    
    setLoading(true);
    
    try {
      if (item) {
        console.log('Updating item:', item[idField], formData);
        await api.update(item[idField], formData);
      } else {
        console.log('Creating item:', formData);
        await api.create(formData);
      }
      
      setHasChanges(false);
      onClose(true);
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.response?.data?.error || t('messages.error_save', 'Failed to save'));
    } finally {
      setLoading(false);
    }
  };

  // NOW check for missing fields AFTER hooks are called
  if (!fields || !Array.isArray(fields)) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <h3>Configuration Error</h3>
            <button className="modal-close" onClick={() => onClose(false)} type="button">×</button>
          </div>
          <div className="modal-form">
            <div className="form-error">
              <span className="error-icon">⚠️</span>
              Configuration error: fields prop is missing or invalid
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>{title}</h3>
          <button 
            className="modal-close" 
            onClick={handleClose}
            type="button"
            title={t('close_esc', 'Close (ESC)')}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && (
            <div className="form-error">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          {hasChanges && (
            <div className="form-warning">
              <span className="warning-icon">✏️</span>
              {t('messages.unsaved', 'You have unsaved changes')}
            </div>
          )}

          {fields.map((field, index) => (
            <div key={field.name} className="form-group">
              <label>
                {t(field.label)} {field.required && '*'}
              </label>
              {field.type === 'textarea' ? (
                <textarea
                  name={field.name}
                  value={formData[field.name] || ''}
                  onChange={handleChange}
                  placeholder={field.placeholder ? t(field.placeholder) : ''}
                  rows={field.rows || 3}
                  required={field.required}
                  autoFocus={index === 0}
                />
              ) : field.type === 'select' ? (
                <select
                  name={field.name}
                  value={formData[field.name] || ''}
                  onChange={handleChange}
                  required={field.required}
                  autoFocus={index === 0}
                >
                  <option value="">{t('select_option', 'Select...')}</option>
                  {field.options?.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {t(opt.label) || opt.label}
                    </option>
                  ))}
                </select>
              ) : field.type === 'number' ? (
                <input
                  type="number"
                  name={field.name}
                  value={formData[field.name] === '' ? '' : formData[field.name]}
                  onChange={handleChange}
                  placeholder={field.placeholder ? t(field.placeholder) : ''}
                  required={field.required}
                  min={field.min}
                  max={field.max}
                  step={field.step || 1}
                  autoFocus={index === 0}
                />
              ) : field.type === 'checkbox' ? (
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name={field.name}
                    checked={formData[field.name] || false}
                    onChange={handleChange}
                  />
                  <span>{field.checkboxLabel ? t(field.checkboxLabel) : ''}</span>
                </label>
              ) : (
                <input
                  type={field.type || 'text'}
                  name={field.name}
                  value={formData[field.name] || ''}
                  onChange={handleChange}
                  placeholder={field.placeholder ? t(field.placeholder) : ''}
                  required={field.required}
                  maxLength={field.maxLength}
                  autoFocus={index === 0}
                />
              )}
            </div>
          ))}

          <div className="modal-footer">
            <button 
              type="button" 
              onClick={handleClose}
              className="btn-secondary"
              disabled={loading}
            >
              {t('cancel', 'Cancel')}
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={loading}
            >
              {loading 
                ? t('saving', 'Saving...') 
                : t('save', 'Save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MasterDataModal;